import React, { useEffect, useMemo } from 'react';

// Auto-detect environment from current hostname
const getEnvironmentInfo = () => {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return { name: 'development', label: 'Local Development (localhost:8000)', badge: 'LOCAL', isProduction: false };
  } else if (hostname === 'staging.optionsleveling.com') {
    return { name: 'staging', label: 'Staging (Railway)', badge: 'STAGING', isProduction: true };
  } else if (hostname === 'app.optionsleveling.com') {
    return { name: 'production', label: 'Production (Railway)', badge: 'PROD', isProduction: true };
  } else if (hostname === 'optionsleveling.com') {
    return { name: 'legacy', label: 'Legacy (Cloud Run)', badge: 'LEGACY', isProduction: true };
  }
  // Fallback for any other domain
  return { name: 'unknown', label: hostname, badge: 'OTHER', isProduction: true };
};

function TestRunner({
  environment,
  setEnvironment,
  testTypes,
  selectedTests,
  toggleTestSelection,
  selectAllTests,
  deselectAllTests,
  selectCategory,
  handleRunTests,
  isRunning
}) {
  // Auto-detect environment on mount
  const envInfo = useMemo(() => getEnvironmentInfo(), []);

  useEffect(() => {
    // Set environment based on detected hostname
    setEnvironment(envInfo.name);
  }, [envInfo.name, setEnvironment]);

  // Group tests by category
  const testsByCategory = testTypes.reduce((acc, test) => {
    if (!acc[test.category]) {
      acc[test.category] = [];
    }
    acc[test.category].push(test);
    return acc;
  }, {});

  return (
    <div className="test-runner">
      <div className="runner-controls">
        <div className="environment-selector">
          <label>Testing Against:</label>
          <span className="env-info">{envInfo.label}</span>
          <span className={`env-badge ${envInfo.name}`}>
            {envInfo.badge}
          </span>
        </div>

        <div className="selection-controls">
          <button onClick={selectAllTests} disabled={isRunning}>
            Select All
          </button>
          <button onClick={deselectAllTests} disabled={isRunning}>
            Clear Selection
          </button>
        </div>
      </div>

      <div className="test-selection">
        <h3>Select Tests to Run</h3>
        {Object.entries(testsByCategory).map(([category, tests]) => (
          <div key={category} className="test-category">
            <div className="category-header">
              <h4>{category}</h4>
              <button
                className="select-category-btn"
                onClick={() => selectCategory(category)}
                disabled={isRunning}
              >
                Select All {category}
              </button>
            </div>
            <div className="test-list">
              {tests.map(test => (
                <label key={test.id} className="test-item">
                  <input
                    type="checkbox"
                    checked={selectedTests.includes(test.id)}
                    onChange={() => toggleTestSelection(test.id)}
                    disabled={isRunning}
                  />
                  <span className="test-name">{test.name}</span>
                  <span className="test-info" title={test.description}>
                    ℹ️
                  </span>
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="run-controls">
        <button
          className="run-button"
          onClick={handleRunTests}
          disabled={isRunning || selectedTests.length === 0}
        >
          {isRunning ? 'Running Tests...' : `Run ${selectedTests.length || 'All'} Tests`}
        </button>
        {!envInfo.isProduction && (
          <div className="info-message">
            <p>ℹ️ <strong>Local Testing:</strong></p>
            <p>• Tests run against your local Docker containers (localhost:8000)</p>
            <p>• All test types are safe to run in development</p>
            <p>• Test data is created and cleaned up automatically</p>
          </div>
        )}
        {envInfo.isProduction && (
          <div className="warning-message">
            <p>⚠️ <strong>{envInfo.badge} Environment Testing:</strong></p>
            <p>• Tests will run against: {envInfo.label}</p>
            <p>• Validation tests (prod_*) are read-only and safe</p>
            <p>• Functional tests will create temporary test data (prefixed with TEST_)</p>
            <p>• All test data is automatically cleaned up after execution</p>
            <p>• Tests use dedicated test account to avoid affecting real users</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default TestRunner;